// RepoScopeToggle — the "this repo ↔ all projects" search-scope control
// (RFC-0011 Task 1.6, PD-0017 P5). Promoted into Phase 1 because both surfaces
// use it: the Cmd-K palette renders it as a scope chip, and the `/search` page
// renders it in the filter rail.
//
// Controlled + presentational: it owns no state and does no data fetching. The
// parent holds `scope` and maps it to the API `repo` param — `'all'` omits
// `repo` (search every project); `'repo'` sends the bare short name (`repoName`).
// When no `repoName` is available (e.g. the `/search` default), only the
// "All projects" option renders and scope is effectively `'all'`.

interface RepoScopeToggleProps {
  /** Current scope. `'repo'` requires `repoName`. */
  scope: 'repo' | 'all';
  /** Repo SHORT name for the "this repo" option; when absent, only "All projects" shows. */
  repoName?: string;
  /** Called with the next scope when the user picks an option. */
  onChange: (next: 'repo' | 'all') => void;
  className?: string;
}

const OPTION_BASE =
  'inline-flex min-h-[40px] items-center rounded-md px-3 py-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';
const OPTION_ACTIVE = 'bg-accent text-on-accent';
const OPTION_INACTIVE = 'text-muted hover:text-text';

export function RepoScopeToggle({
  scope,
  repoName,
  onChange,
  className = '',
}: RepoScopeToggleProps) {
  const repoSelected = scope === 'repo' && Boolean(repoName);
  const allSelected = !repoSelected;

  return (
    <div
      role="group"
      aria-label="Search scope"
      className={[
        'inline-flex items-center gap-0.5 rounded-lg border border-border bg-accent-faint p-0.5',
        className,
      ]
        .join(' ')
        .trim()}
    >
      {repoName && (
        <button
          type="button"
          aria-pressed={repoSelected}
          onClick={() => onChange('repo')}
          className={`${OPTION_BASE} ${repoSelected ? OPTION_ACTIVE : OPTION_INACTIVE}`}
          title={`Search only ${repoName}`}
        >
          {repoName}
        </button>
      )}
      <button
        type="button"
        aria-pressed={allSelected}
        onClick={() => onChange('all')}
        className={`${OPTION_BASE} ${allSelected ? OPTION_ACTIVE : OPTION_INACTIVE}`}
        title="Search all projects"
      >
        All projects
      </button>
    </div>
  );
}
